import { createFileRoute } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";

const SITE_URL = "https://coloradowebimpressions.com";
const LAST_UPDATED = "April 26, 2026";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service | Colorado Web Impressions" },
      {
        name: "description",
        content:
          "Terms of Service for Colorado Web Impressions — the plain-English rules for using our website and working with our Colorado Springs digital marketing agency.",
      },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Terms of Service | Colorado Web Impressions" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/terms` }],
    scripts: [
      buildBreadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Terms of Service", path: "/terms" },
      ]),
    ],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Legal"
        title="Terms of Service"
        description={`Last updated: ${LAST_UPDATED}. These terms cover both your use of this website and any client engagement with Colorado Web Impressions.`}
      />

      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <article className="prose-article">
            <h2>Agreement</h2>
            <p>
              By using coloradowebimpressions.com or hiring Heidlebaugh Digital
              LLC (dba Colorado Web Impressions; &ldquo;we,&rdquo;
              &ldquo;us,&rdquo; &ldquo;our&rdquo;) for services, you agree to
              these Terms. If you don&apos;t agree, please don&apos;t use the
              site or hire us.
            </p>
            <p>
              <strong>Legal entity:</strong> Heidlebaugh Digital LLC dba Colorado
              Web Impressions, 3210 E Woodmen Rd Ste 110, Colorado Springs, CO 80920.
            </p>

            <h2>Services</h2>
            <p>
              We provide web design, SEO, Google Business Profile optimization,
              PPC management, and related digital marketing services. The
              specific scope, deliverables, timeline, and price for any
              engagement are set in a separate written proposal or statement
              of work signed by both parties. That document controls in case of
              any conflict with this page.
            </p>

            <h2>Payment</h2>
            <p>
              Project fees are typically split between an upfront deposit and
              milestone or monthly payments. Recurring services (SEO, PPC
              management, hosting) are billed monthly in advance. Late payments
              may pause active work until the account is current.
            </p>

            <h2>Ownership</h2>
            <ul>
              <li>
                <strong>Your content:</strong> any text, images, or brand
                materials you give us remain 100% yours.
              </li>
              <li>
                <strong>Final deliverables:</strong> upon final payment, the
                finished website and copy we create for you become your
                property.
              </li>
              <li>
                <strong>Our methods & internal tools:</strong> our
                pre-existing code libraries, internal documentation, and
                proprietary methods remain ours.
              </li>
            </ul>

            <h2>Results & guarantees</h2>
            <p>
              We don&apos;t guarantee specific rankings, traffic numbers, or
              revenue outcomes. Anyone who does is not telling the truth.
              We do guarantee professional, in-house workmanship and that
              we&apos;ll do exactly what&apos;s in your signed scope of work.
            </p>

            <h2>Cancellation</h2>
            <p>
              Month-to-month services can be cancelled with 30 days written
              notice. Project work can be paused or cancelled at any time;
              you&apos;ll be invoiced for completed work and any non-refundable
              third-party costs already incurred on your behalf.
            </p>

            <h2>Liability</h2>
            <p>
              To the fullest extent allowed by law, our total liability for any
              claim arising from your use of the website or our services is
              limited to the amount you paid us in the 12 months preceding the
              claim. We are not liable for indirect, incidental, or
              consequential damages.
            </p>

            <h2>Governing law</h2>
            <p>
              These Terms are governed by the laws of the State of Colorado.
              Any dispute will be resolved in the state or federal courts
              located in El Paso County, Colorado.
            </p>

            <h2>Contact</h2>
            <p>
              Questions? Email{" "}
              <a href="mailto:info@coloradowebimpressions.com" aria-label="Email Colorado Web Impressions">
                info@coloradowebimpressions.com
              </a>{" "}
              or call <a href="tel:+17194193935" aria-label="Call (719) 419-3935">(719) 419-3935</a>.
            </p>
          </article>
        </div>
      </section>
    </SiteLayout>
  );
}
